import {
	DEFAULT_TRAJECTORY_PARAMS,
	type TrajectoryType,
} from "./trajectory/types";
import { GaussianViewer } from "./viewer/GaussianViewer";

console.log("[main] Script loaded");

// Get DOM elements
const containerElement = document.getElementById("canvas-container");
console.log("[main] Container element:", containerElement);
const fileLoaderElement = document.getElementById("file-loader");
const fileInputElement = document.getElementById(
	"file-input",
) as HTMLInputElement;
const trajectorySelectElement = document.getElementById(
	"trajectory-select",
) as HTMLSelectElement;
const playButtonElement = document.getElementById(
	"play-btn",
) as HTMLButtonElement;
const pauseButtonElement = document.getElementById(
	"pause-btn",
) as HTMLButtonElement;
const resetButtonElement = document.getElementById(
	"reset-btn",
) as HTMLButtonElement;
const loadingElement = document.getElementById("loading");
const loadSampleButtonElement = document.getElementById(
	"load-sample-btn",
) as HTMLButtonElement;
const imageLoaderElement = document.getElementById(
	"image-loader",
) as HTMLDivElement;
const imageInputElement = document.getElementById(
	"image-input",
) as HTMLInputElement;
const imageLoaderTitleElement = document.getElementById(
	"image-loader-title",
) as HTMLSpanElement;
const passwordInputElement = document.getElementById(
	"password-input",
) as HTMLInputElement;
const generateButtonElement = document.getElementById(
	"generate-btn",
) as HTMLButtonElement;
const jobStatusElement = document.getElementById(
	"job-status",
) as HTMLDivElement;
const jobStatusTextElement = document.getElementById(
	"job-status-text",
) as HTMLSpanElement;
const progressBarElement = document.getElementById(
	"progress-bar",
) as HTMLSpanElement;
const loadingTextElement = document.getElementById(
	"loading-text",
) as HTMLSpanElement;

const API_BASE = ["localhost", "127.0.0.1"].includes(window.location.hostname)
	? "http://127.0.0.1:8787"
	: "https://forest-api.yshicarrick.com";

interface JobStatus {
	id: string;
	status: "queued" | "running" | "complete" | "error";
	stage: string;
	progress: number;
	error?: string | null;
	result_url?: string;
	width?: number;
	height?: number;
	focal_length?: number;
}

let selectedImageFile: File | null = null;
let isGenerating = false;

// Advanced settings elements
const advancedToggleElement = document.getElementById(
	"advanced-toggle",
) as HTMLButtonElement;
const advancedPanelElement = document.getElementById(
	"advanced-panel",
) as HTMLDivElement;
const maxDisparityInputElement = document.getElementById(
	"max-disparity-input",
) as HTMLInputElement;
const maxZoomInputElement = document.getElementById(
	"max-zoom-input",
) as HTMLInputElement;
const distanceInputElement = document.getElementById(
	"distance-input",
) as HTMLInputElement;
const numStepsInputElement = document.getElementById(
	"num-steps-input",
) as HTMLInputElement;
const numRepeatsInputElement = document.getElementById(
	"num-repeats-input",
) as HTMLInputElement;
const resetParamsButtonElement = document.getElementById(
	"reset-params-btn",
) as HTMLButtonElement;

if (!containerElement) {
	throw new Error("Canvas container not found");
}

console.log("[main] Initializing GaussianViewer...");

// Initialize viewer
const viewer = new GaussianViewer({
	container: containerElement,
	onLoad: () => {
		console.log("[main] Splat loaded successfully");
		hideLoading();
		enableControls();
	},
	onError: (error) => {
		console.error("[main] Failed to load splat:", error);
		hideLoading();
		alert(`Failed to load 3D scene: ${error.message}`);
	},
	onTrajectoryStateChange: (state) => {
		console.log("[main] Trajectory state changed:", state);
		updateButtonStates(state);
	},
	onFrameChange: (_frame, _total) => {
		// Could add a progress indicator here
	},
	// Canvas stays fixed size - splat renders with empty space around it as needed
});

console.log("[main] GaussianViewer initialized");

// UI State Management
function showLoading(message = "正在加载 3D 场景…"): void {
	loadingTextElement.textContent = message;
	loadingElement?.classList.add("visible");
}

function hideLoading(): void {
	loadingElement?.classList.remove("visible");
}

function setParameterControlsDisabled(disabled: boolean): void {
	advancedToggleElement.disabled = disabled;
	maxDisparityInputElement.disabled = disabled;
	maxZoomInputElement.disabled = disabled;
	distanceInputElement.disabled = disabled;
	numStepsInputElement.disabled = disabled;
	numRepeatsInputElement.disabled = disabled;
	resetParamsButtonElement.disabled = disabled;
}

function enableControls(): void {
	trajectorySelectElement.disabled = false;
	playButtonElement.disabled = false;
	pauseButtonElement.disabled = false;
	resetButtonElement.disabled = false;
	setParameterControlsDisabled(false);
}

function disableControls(): void {
	trajectorySelectElement.disabled = true;
	playButtonElement.disabled = true;
	pauseButtonElement.disabled = true;
	resetButtonElement.disabled = true;
	setParameterControlsDisabled(true);
}

function updateButtonStates(state: "stopped" | "playing" | "paused"): void {
	playButtonElement.disabled = state === "playing";
	pauseButtonElement.disabled = state !== "playing";
	// Disable parameter controls during playback
	setParameterControlsDisabled(state === "playing");
}

// File Loading
async function loadFile(file: File): Promise<void> {
	console.log("[main] loadFile called with:", file.name, file.size, "bytes");

	if (!/\.(ply|spz)$/i.test(file.name)) {
		alert("请选择 PLY 或 SPZ 文件");
		return;
	}

	showLoading();
	disableControls();

	try {
		console.log("[main] Calling viewer.loadSplat...");
		await viewer.loadSplat(file);
		console.log("[main] viewer.loadSplat completed");
	} catch (error) {
		console.error("[main] loadFile error:", error);
		// Error already handled in viewer's onError callback
	}
}

// Sample File Loading
async function loadSampleFile(): Promise<void> {
	const sampleFileUrl = `${import.meta.env.BASE_URL}samples/forest.spz`;
	console.log("[main] Loading sample file from:", sampleFileUrl);

	showLoading();
	disableControls();

	try {
		const response = await fetch(sampleFileUrl);
		if (!response.ok) {
			throw new Error(`Failed to fetch sample: HTTP ${response.status}`);
		}
		const blob = await response.blob();
		const file = new File([blob], "forest.spz", {
			type: "application/octet-stream",
		});
		await viewer.loadSplat(file);
	} catch (error) {
		hideLoading();
		console.error("[main] Failed to load sample file:", error);
		const errorMessage = error instanceof Error ? error.message : String(error);
		alert(`Failed to load sample file: ${errorMessage}`);
	}
}

function updateGenerateButton(): void {
	generateButtonElement.disabled =
		isGenerating ||
		!selectedImageFile ||
		passwordInputElement.value.length === 0;
}

function selectImage(file: File): void {
	if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
		alert("请选择 JPG、PNG 或 WebP 图片");
		return;
	}
	if (file.size > 20 * 1024 * 1024) {
		alert("图片不能超过 20 MB");
		return;
	}
	selectedImageFile = file;
	imageLoaderTitleElement.textContent = file.name;
	imageLoaderElement.classList.add("has-file");
	updateGenerateButton();
}

function setJobStatus(
	message: string,
	progress: number,
	isError = false,
): void {
	jobStatusElement.hidden = false;
	jobStatusElement.classList.toggle("error", isError);
	jobStatusTextElement.textContent = message;
	progressBarElement.style.width = `${Math.max(0, Math.min(100, progress))}%`;
}

async function readApiError(response: Response): Promise<string> {
	try {
		const body = (await response.json()) as { detail?: string };
		return body.detail ?? `HTTP ${response.status}`;
	} catch {
		return `HTTP ${response.status}`;
	}
}

async function sleep(milliseconds: number): Promise<void> {
	await new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function generateScene(): Promise<void> {
	if (!selectedImageFile || isGenerating) return;
	const password = passwordInputElement.value;
	if (!password) return;

	isGenerating = true;
	updateGenerateButton();
	setJobStatus("正在安全上传照片…", 4);

	try {
		const form = new FormData();
		form.append("image", selectedImageFile);
		const createResponse = await fetch(`${API_BASE}/v1/jobs`, {
			method: "POST",
			headers: { "X-Forest-Password": password },
			body: form,
		});
		if (!createResponse.ok) {
			throw new Error(await readApiError(createResponse));
		}
		const created = (await createResponse.json()) as { id: string };
		sessionStorage.setItem("forest-password", password);

		let job: JobStatus | null = null;
		for (let attempt = 0; attempt < 300; attempt += 1) {
			await sleep(1500);
			const statusResponse = await fetch(`${API_BASE}/v1/jobs/${created.id}`, {
				headers: { "X-Forest-Password": password },
			});
			if (!statusResponse.ok) {
				throw new Error(await readApiError(statusResponse));
			}
			job = (await statusResponse.json()) as JobStatus;
			setJobStatus(job.stage, job.progress);
			if (job.status === "complete" || job.status === "error") break;
		}

		if (!job || job.status !== "complete" || !job.result_url) {
			throw new Error(job?.error ?? "生成超时，请重试");
		}

		setJobStatus("正在下载并打开 3D 场景…", 100);
		showLoading("正在进入你的 3D 场景…");
		disableControls();
		const sceneResponse = await fetch(`${API_BASE}${job.result_url}`, {
			headers: { "X-Forest-Password": password },
		});
		if (!sceneResponse.ok) {
			throw new Error(await readApiError(sceneResponse));
		}
		const sceneBlob = await sceneResponse.blob();
		const sceneFile = new File([sceneBlob], "generated-scene.spz", {
			type: "application/octet-stream",
		});
		await viewer.loadSplat(sceneFile, {
			imageSize: [job.width ?? 1280, job.height ?? 960],
			focalLength: job.focal_length ?? 1109.4004,
		});
		setJobStatus("完成 · 现在可以走进照片了", 100);
	} catch (error) {
		hideLoading();
		const message = error instanceof Error ? error.message : String(error);
		setJobStatus(message, 0, true);
	} finally {
		isGenerating = false;
		updateGenerateButton();
	}
}

// Event Listeners
fileInputElement?.addEventListener("change", (event) => {
	const target = event.target as HTMLInputElement;
	const file = target.files?.[0];
	if (file) {
		loadFile(file);
	}
});

fileLoaderElement?.addEventListener("click", () => {
	fileInputElement?.click();
});

imageInputElement?.addEventListener("change", () => {
	const file = imageInputElement.files?.[0];
	if (file) selectImage(file);
});

imageLoaderElement?.addEventListener("click", () => imageInputElement.click());
imageLoaderElement?.addEventListener("keydown", (event) => {
	if (event.key === "Enter" || event.key === " ") {
		event.preventDefault();
		imageInputElement.click();
	}
});
imageLoaderElement?.addEventListener("dragover", (event) => {
	event.preventDefault();
	imageLoaderElement.classList.add("drag-over");
});
imageLoaderElement?.addEventListener("dragleave", () => {
	imageLoaderElement.classList.remove("drag-over");
});
imageLoaderElement?.addEventListener("drop", (event) => {
	event.preventDefault();
	imageLoaderElement.classList.remove("drag-over");
	const file = event.dataTransfer?.files[0];
	if (file) selectImage(file);
});

passwordInputElement.value = sessionStorage.getItem("forest-password") ?? "";
passwordInputElement.addEventListener("input", updateGenerateButton);
generateButtonElement.addEventListener("click", () => void generateScene());

// Drag and drop
fileLoaderElement?.addEventListener("dragover", (event) => {
	event.preventDefault();
	fileLoaderElement.classList.add("drag-over");
});

fileLoaderElement?.addEventListener("dragleave", () => {
	fileLoaderElement.classList.remove("drag-over");
});

fileLoaderElement?.addEventListener("drop", (event) => {
	event.preventDefault();
	fileLoaderElement.classList.remove("drag-over");

	const file = event.dataTransfer?.files[0];
	if (file) {
		loadFile(file);
	}
});

// Load sample button
loadSampleButtonElement?.addEventListener("click", () => {
	loadSampleFile();
});

// Trajectory controls
trajectorySelectElement?.addEventListener("change", () => {
	const type = trajectorySelectElement.value as TrajectoryType;
	viewer.setTrajectoryType(type);
});

playButtonElement?.addEventListener("click", () => {
	viewer.play();
});

pauseButtonElement?.addEventListener("click", () => {
	viewer.pause();
});

resetButtonElement?.addEventListener("click", () => {
	viewer.reset();
});

// Advanced settings toggle
advancedToggleElement?.addEventListener("click", () => {
	const isExpanded = advancedToggleElement.classList.toggle("expanded");
	advancedToggleElement.setAttribute("aria-expanded", String(isExpanded));
	advancedPanelElement?.classList.toggle("collapsed", !isExpanded);
});

// Wire up parameter controls
maxDisparityInputElement?.addEventListener("input", () => {
	const value = Number.parseFloat(maxDisparityInputElement.value);
	if (!Number.isNaN(value)) {
		viewer.updateTrajectoryParam("maxDisparity", value);
	}
});

maxZoomInputElement?.addEventListener("input", () => {
	const value = Number.parseFloat(maxZoomInputElement.value);
	if (!Number.isNaN(value)) {
		viewer.updateTrajectoryParam("maxZoom", value);
	}
});

distanceInputElement?.addEventListener("input", () => {
	const value = Number.parseFloat(distanceInputElement.value);
	if (!Number.isNaN(value)) {
		viewer.updateTrajectoryParam("distanceMeters", value);
	}
});

numStepsInputElement?.addEventListener("input", () => {
	const value = Number.parseInt(numStepsInputElement.value, 10);
	if (!Number.isNaN(value)) {
		viewer.updateTrajectoryParam("numSteps", value);
	}
});

numRepeatsInputElement?.addEventListener("input", () => {
	const value = Number.parseInt(numRepeatsInputElement.value, 10);
	if (!Number.isNaN(value)) {
		viewer.updateTrajectoryParam("numRepeats", value);
	}
});

// Reset to defaults
function updateParameterInputsFromDefaults(): void {
	const defaults = DEFAULT_TRAJECTORY_PARAMS;
	maxDisparityInputElement.value = String(defaults.maxDisparity);
	maxZoomInputElement.value = String(defaults.maxZoom);
	distanceInputElement.value = String(defaults.distanceMeters);
	numStepsInputElement.value = String(defaults.numSteps);
	numRepeatsInputElement.value = String(defaults.numRepeats);
}

resetParamsButtonElement?.addEventListener("click", () => {
	viewer.resetTrajectoryParams();
	updateParameterInputsFromDefaults();
});

// Keyboard shortcuts
document.addEventListener("keydown", (event) => {
	if (!viewer.isLoaded()) return;

	switch (event.key) {
		case " ":
			event.preventDefault();
			if (viewer.getPlayerState() === "playing") {
				viewer.pause();
			} else {
				viewer.play();
			}
			break;
		case "r":
		case "R":
			viewer.reset();
			break;
		case "Escape":
			viewer.stop();
			break;
	}
});

// Check for URL parameter to auto-load a file
const urlParams = new URLSearchParams(window.location.search);
const fileUrl = urlParams.get("file");

// This project ships with the user's generated forest scene, so load it immediately.
if (!fileUrl) {
	void loadSampleFile();
}

if (fileUrl) {
	showLoading();
	fetch(fileUrl)
		.then((response) => {
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			return response.blob();
		})
		.then((blob) => {
			const file = new File([blob], "scene.ply", {
				type: "application/octet-stream",
			});
			return loadFile(file);
		})
		.catch((error) => {
			hideLoading();
			console.error("Failed to load file from URL:", error);
		});
}
